import React from 'react';

import type { SidebarBrandProps } from './Sidebar.types';

import { Logo } from '@/components/ui/Logo';
import { SectionLabel } from '@/components/ui/primitives';

/**
 * Brand header section for the sidebar.
 *
 * Displays:
 * - The 40px drawn Dō mark
 * - Gradient brand name (e.g., "Dō")
 * - Optional tagline (e.g., "Personal Training")
 *
 * When collapsed, only the mark is visible, and it carries the brand name.
 *
 * @example
 * ```tsx
 * <SidebarBrand
 *   brand={{ name: 'Dō', tagline: 'Personal Training' }}
 *   collapsed={false}
 * />
 * ```
 */
export function SidebarBrand({
  brand,
  collapsed = false,
}: SidebarBrandProps): React.JSX.Element {
  return (
    <div
      className={`
        flex h-16 items-center border-b border-stroke-default px-4
        ${collapsed ? 'justify-center' : 'gap-3'}
      `}
    >
      {/* The mark names the brand only while the name is hidden */}
      <Logo
        size={40}
        title={brand.name}
        decorative={!collapsed}
        className="flex-shrink-0"
      />

      {/* Brand text (hidden when collapsed) */}
      {!collapsed && (
        <div className="flex flex-col">
          <span
            className="
              bg-gradient-bloom
              bg-clip-text text-body-xl font-bold tracking-tight text-transparent
            "
          >
            {brand.name}
          </span>
          {brand.tagline && (
            <SectionLabel as="span">{brand.tagline}</SectionLabel>
          )}
        </div>
      )}
    </div>
  );
}

export default SidebarBrand;
